import type { UUID } from '@/types/Id'

// ─────────────────────────────────────────────────────────────────────────────
// Core enums / unions
// ─────────────────────────────────────────────────────────────────────────────

export type MonitorStatus = 'active' | 'paused'

export type MonitorRecipeKind = 'activity' | 'track'

/** Public source vocabulary — the API never exposes vendor names. */
export type MonitorProvider = 'federal' | 'web'

export const MONITOR_RECIPE_TARGETS = [
  'vendor',
  'agency',
  'opportunity',
  'contract',
  'idv',
  'ota',
  'otidv',
  'grant',
  'forecast',
  'web',
  'website',
] as const

export type MonitorRecipeTarget = (typeof MONITOR_RECIPE_TARGETS)[number]

/** Targets the monitor target-search endpoint can look up (web/website have no
 * upstream resource to search for). */
export type MonitorTargetSearchKind = Exclude<
  MonitorRecipeTarget,
  'web' | 'website'
>

/** Frontend-only grouping of recipes into the wizard's three start paths. */
export type MonitorWorkflow = 'entity' | 'resource' | 'web'

export type MonitorRecipeKey =
  | 'monitor.vendor.activity'
  | 'monitor.agency.activity'
  | 'monitor.opportunity.track'
  | 'monitor.contract.track'
  | 'monitor.idv.track'
  | 'monitor.ota.track'
  | 'monitor.otidv.track'
  | 'monitor.grant.track'
  | 'monitor.forecast.track'
  | 'monitor.web.track'
  | 'monitor.website.track'

// ─────────────────────────────────────────────────────────────────────────────
// Recipe catalog (GET /organizations/{orgId}/monitors/recipes — live)
// ─────────────────────────────────────────────────────────────────────────────

export interface CatalogTopic {
  /** Registry topic key, e.g. "vendor.contract_awarded". */
  key: string
  /** Human label from the backend catalog (the wire field is `label`, never
   * `name`/`description` — short helper copy is layered on in the FE via
   * `topicPresentation`). */
  label: string
  provider: MonitorProvider
  /** Name of the recipe-params field holding this topic's tunable scope; null
   * means the topic is not tunable. */
  params_field: string | null
}

export interface CatalogRecipe {
  /** "monitor.{target}.{kind}" */
  key: string
  target: MonitorRecipeTarget
  /** Human label from the backend catalog (the wire field is `label`). */
  label: string
  topics: CatalogTopic[]
}

// ─────────────────────────────────────────────────────────────────────────────
// Topic scopes (mirror the backend topic-form models in
// common_services/monitors/definitions/base/forms.py).
// All fields optional; the backend materializes omitted fields as null in
// `recipe_params` responses. Wire values are plain string lists / scalars —
// `search` is a keyword list, single-choice code fields are one string.
// ─────────────────────────────────────────────────────────────────────────────

export interface ContractScope {
  search?: string[] | null
  obligated_gte?: number | null
  naics?: string[] | null
  psc?: string[] | null
  set_aside?: string[] | null
  // Multi-value on the form; the backend fans each code out into its own
  // upstream subscription (any selected type matches). Legacy monitors may
  // hold a bare scalar code.
  award_type?: string[] | string | null
}

export interface IdvScope {
  search?: string[] | null
  naics?: string[] | null
  psc?: string[] | null
  set_aside?: string[] | null
  idv_type?: string[] | string | null
}

export interface OtaScope {
  search?: string[] | null
  psc?: string[] | null
}

export interface OpportunityScope {
  search?: string[] | null
  naics?: string[] | null
  psc?: string[] | null
  set_aside?: string[] | null
  notice_type?: string[] | null
  place_of_performance?: string[] | null
}

export interface GrantScope {
  search?: string[] | null
  cfda_number?: string | null
  // Multi-value on the form; the backend fans each code out into its own
  // upstream subscription (any selected code matches). Legacy monitors may
  // hold bare scalar codes.
  applicant_types?: string[] | string | null
  funding_categories?: string[] | string | null
  funding_instruments?: string[] | string | null
  status?: string[] | string | null
}

export interface ForecastScope {
  search?: string[] | null
  naics_code?: string[] | null
  naics_starts_with?: string[] | null
  fiscal_year_gte?: number | null
}

// ─────────────────────────────────────────────────────────────────────────────
// Recipe params — discriminated union on `recipe_key`. Backend models are
// frozen `extra="forbid"`: never send fields that aren't listed here.
// ─────────────────────────────────────────────────────────────────────────────

export interface VendorActivityParams {
  recipe_key: 'monitor.vendor.activity'
  uei: string
  contract_awarded?: ContractScope
  idv_awarded?: IdvScope
  ota_awarded?: OtaScope
  otidv_awarded?: OtaScope
}

export interface AgencyActivityParams {
  recipe_key: 'monitor.agency.activity'
  agency: string
  opportunity_posted?: OpportunityScope
  contract_awarded?: ContractScope
  contract_funded?: ContractScope
  grant_posted?: GrantScope
  forecast_posted?: ForecastScope
}

export interface OpportunityTrackParams {
  recipe_key: 'monitor.opportunity.track'
  opportunity_id: string
}

export interface ContractTrackParams {
  recipe_key: 'monitor.contract.track'
  contract_key: string
}

export interface IdvTrackParams {
  recipe_key: 'monitor.idv.track'
  idv_key: string
}

export interface OtaTrackParams {
  recipe_key: 'monitor.ota.track'
  ota_key: string
}

export interface OtidvTrackParams {
  recipe_key: 'monitor.otidv.track'
  otidv_key: string
}

export interface GrantTrackParams {
  recipe_key: 'monitor.grant.track'
  /** Numeric string — the backend int()-casts it. Validate digits-only. */
  grant_id: string
}

export interface ForecastTrackParams {
  recipe_key: 'monitor.forecast.track'
  /** Numeric string — the backend int()-casts it. Validate digits-only. */
  forecast_id: string
}

export interface WebTrackParams {
  recipe_key: 'monitor.web.track'
  query: string
}

export interface WebsiteTrackParams {
  recipe_key: 'monitor.website.track'
  url: string
}

/**
 * Fields shared by every recipe's params (mirrors the backend `RecipeForm`
 * base). Kept as an intersection so each member stays narrowable on
 * `recipe_key`.
 */
export interface MonitorParamsCommon {
  /**
   * Topic keys the user has switched OFF. Omitted/empty means every topic is
   * on. Disabling a topic drops it from the compiled set, so its subscription
   * is deregistered upstream (and re-registered fresh if re-enabled).
   * Single-topic recipes don't expose this — use `status: 'paused'` to silence
   * those entirely. Canonicalized (sorted/deduped) server-side.
   */
  disabled_topics?: string[]
}

export type MonitorParams = (
  | VendorActivityParams
  | AgencyActivityParams
  | OpportunityTrackParams
  | ContractTrackParams
  | IdvTrackParams
  | OtaTrackParams
  | OtidvTrackParams
  | GrantTrackParams
  | ForecastTrackParams
  | WebTrackParams
  | WebsiteTrackParams
) &
  MonitorParamsCommon

// ─────────────────────────────────────────────────────────────────────────────
// Monitor wire types
// ─────────────────────────────────────────────────────────────────────────────

export interface MonitorOut {
  id: UUID
  organization_id: UUID
  name: string
  recipe_key: string
  /** Raw on the response side (the backend returns a plain dict); re-narrow on
   * `recipe_params.recipe_key` when a typed view is needed. */
  recipe_params: Record<string, unknown>
  status: MonitorStatus
  slack_channel_id: UUID | null
  email_subscriber_ids: UUID[]
  created_by_user_id: UUID | null
  created_at: Date | string
}

/**
 * Overview read model returned by the live GET list/single-monitor endpoints.
 * The overview stats are optional only because POST/PATCH/DELETE keep
 * returning plain `MonitorOut` and caches merge those responses over cached
 * overview objects — every consumer must tolerate their absence.
 */
/**
 * Newest feed entry for a monitor (any status), as shipped in the overview
 * list. Drives "most recent notice" ordering and the relative-time label in
 * the inbox rail.
 */
export interface MonitorFeedEntryDigest {
  id: UUID
  title: string | null
  resource_kind: string | null
  /** UTC ISO timestamp of the entry's event. */
  event_at: string
}

export interface MonitorOverviewOut extends MonitorOut {
  /** Non-trashed entries with no read receipt for the requesting user. */
  unread_count?: number
  /** Exactly 30 UTC-day counts, oldest first, ending today; counts ALL
   * entries regardless of status (history is immutable). */
  daily_feed_entry_counts?: number[]
  /** Newest feed entry (any status); absent on plain MonitorOut cache merges. */
  latest_entry?: MonitorFeedEntryDigest | null
}

export interface MonitorCreate {
  name: string
  params: MonitorParams
  slack_channel_id?: UUID | null
  email_subscriber_ids?: UUID[]
}

export interface MonitorUpdate {
  name?: string
  params?: MonitorParams
  slack_channel_id?: UUID | null
  email_subscriber_ids?: UUID[]
  status?: MonitorStatus
}

// ─────────────────────────────────────────────────────────────────────────────
// Feed entries (Gmail model: status folder org-wide; read/bookmark per-user)
// ─────────────────────────────────────────────────────────────────────────────

export type MonitorFeedEntryStatus = 'inbox' | 'archived' | 'trashed'

/**
 * Server-side feed sort. `recent` is event_at-DESC; `monitor` groups entries by
 * their owning monitor (org-wide feed only). Mirrors the backend `sort` param.
 */
export type MonitorFeedSort = 'recent' | 'monitor'

/** Canonical ordered list of sort values — single source for the nuqs parser
 * and the sort tab strip (keep in lockstep with `MonitorFeedSort`). */
export const MONITOR_FEED_SORTS: MonitorFeedSort[] = ['recent', 'monitor']

export interface MonitorFeedEntryOut {
  id: UUID
  monitor_id: UUID
  /**
   * Owning monitor's name. Populated ONLY on the org-wide feed endpoint; the
   * per-monitor feed leaves it null (the monitor is already in scope there).
   */
  monitor_name: string | null
  event_at: Date | string
  status: MonitorFeedEntryStatus
  title: string | null
  summary: string | null
  labels: string[]
  pursuit_id: UUID | null
  is_read: boolean
  is_bookmarked: boolean
  resource_kind: string | null
  /**
   * The event's upstream source (federal/web) — every entry has one event,
   * so this is never null. Distinct from `resource_kind`: a web event with
   * no known kind prefix still derives to the `web` kind, but the source
   * itself is needed to badge/route web entries.
   */
  provider: MonitorProvider
  /**
   * Registry topic key that matched this event into the monitor (the "match
   * criteria"), e.g. "vendor.contract_awarded". Null for entries fanned out
   * before the column existed or events with no resolvable topic.
   */
  topic_key: string | null
  due_at: Date | string | null
  posted_at: Date | string | null
  source_url: string | null
}

export interface MonitorFeedStatusCounts {
  inbox: number
  archived: number
  bookmarked: number
  /** Non-trashed total (inbox + archived). */
  all: number
  unread: number
}

/**
 * One resource-kind facet for the rail's "Alert type" list. Server-computed over
 * the active view with the kind filter EXCLUDED, so the count is accurate
 * regardless of pagination and the rail can always show the kinds you could
 * switch to. Only non-zero kinds are returned.
 */
export interface MonitorFeedTypeCount {
  kind: string
  count: number
}

// ─────────────────────────────────────────────────────────────────────────────
// Batch feed mutations (POST .../feed/batch/status, PUT .../feed/batch/read)
// One request mutates many entries. Request bodies are frozen + extra='forbid'
// on the backend and require at least one id (empty bodies 422 at the boundary).
// ─────────────────────────────────────────────────────────────────────────────

export interface MonitorFeedEntryStatusBatchUpdate {
  /** At least one id; foreign/unknown ids silently drop out server-side. */
  entry_ids: UUID[]
  status: MonitorFeedEntryStatus
}

export interface MonitorFeedEntryReadBatchUpdate {
  /** At least one id; foreign/unknown ids silently drop out server-side. */
  entry_ids: UUID[]
  read: boolean
}

/**
 * Result of a batch feed mutation. `entry_ids` are the ids actually affected
 * (in the caller's monitor+org scope) — a subset of the requested ids. No 404:
 * an empty set just means none of the supplied ids were in scope.
 */
export interface MonitorFeedBatchResult {
  count: number
  entry_ids: UUID[]
}

// ─────────────────────────────────────────────────────────────────────────────
// Typed feed payload — mirrors `monitors/ingest/feed/schemas/payload.py` on the
// backend. The event snapshot is validated server-side into a
// `kind`-discriminated facts union (every field optional-by-null;
// unknown/unparseable kinds degrade to `unknown` with the raw snapshot kept),
// shipped with the per-field diff. The grounding types below (WebFactsCitation
// / WebFactsGroundingEntry) are hand-duplicated from the backend web-monitor
// citation/basis schemas — there's no compiler link between the two, so keep
// them in sync by hand when that backend shape changes.
// ─────────────────────────────────────────────────────────────────────────────

export interface MonitorFieldChange {
  field: string
  label: string
  old: unknown
  new: unknown
}

export interface FeedFactsParty {
  uei: string | null
  name: string | null
}

export interface FeedFactsAgency {
  office_name: string | null
  agency_name: string | null
  department_name: string | null
}

export interface FeedFactsPlace {
  city: string | null
  state: string | null
  zip: string | null
  country: string | null
}

export interface FeedFactsContact {
  name: string | null
  title: string | null
  email: string | null
  phone: string | null
}

export interface FeedFactsPerformancePeriod {
  start_date: string | null
  current_end_date: string | null
  ultimate_completion_date: string | null
}

export interface FeedFactsParentAward {
  piid: string | null
  // Derived server-side like the contract's own usaspending_url, so the
  // award-page URL shape lives in exactly one place.
  usaspending_url: string | null
}

interface ContractFactsBase {
  piid: string | null
  description: string | null
  obligated: number | null
  total_value: number | null
  award_date: string | null
  award_type: string | null
  set_aside: string | null
  fiscal_year: number | null
  naics: string | null
  psc: string | null
  solicitation: string | null
  recipient: FeedFactsParty | null
  office: FeedFactsAgency | null
  period_of_performance: FeedFactsPerformancePeriod | null
  place_of_performance: FeedFactsPlace | null
  parent_award: FeedFactsParentAward | null
  // Derived USASpending award-page link (contract/IDV keys are
  // generated_unique_award_ids). Null when the key is missing.
  usaspending_url: string | null
}

export interface ContractFeedFacts extends ContractFactsBase {
  kind: 'contract'
}

export interface IdvFeedFacts extends ContractFactsBase {
  kind: 'idv'
  program_acronym: string | null
  total_estimated_order_value: number | null
  last_date_to_order: string | null
}

interface OtaFactsBase {
  piid: string | null
  award_date: string | null
  award_type: string | null
  fiscal_year: number | null
  value: number | null
  psc: string | null
  consortia: string | null
  recipient: FeedFactsParty | null
  office: FeedFactsAgency | null
  // Derived USASpending award-page link (OTA/OTIDV keys are
  // generated_unique_award_ids). Null when the key is missing.
  usaspending_url: string | null
}

export interface OtaFeedFacts extends OtaFactsBase {
  kind: 'ota'
}

export interface OtidvFeedFacts extends OtaFactsBase {
  kind: 'otidv'
  idv_type: string | null
}

export interface OpportunityFeedFacts {
  kind: 'opportunity'
  opportunity_id: string | null
  title: string | null
  description: string | null
  // Latest notice's SAM.gov type name ("Solicitation", "Sources Sought", …).
  notice_type: string | null
  solicitation: string | null
  set_aside: string | null
  naics: string | null
  psc: string | null
  response_deadline: string | null
  posted_date: string | null
  last_notice_date: string | null
  active: boolean | null
  sam_url: string | null
  office: FeedFactsAgency | null
  place_of_performance: FeedFactsPlace | null
  primary_contact: FeedFactsContact | null
}

export interface GrantFeedFacts {
  kind: 'grant'
  grant_id: number | null
  opportunity_number: string | null
  title: string | null
  description: string | null
  status: string | null
  agency_code: string | null
  office: FeedFactsAgency | null
  posted_date: string | null
  close_date: string | null
  award_ceiling: number | null
  award_floor: number | null
  estimated_total_funding: number | null
  expected_awards: number | null
  grantor_contact: FeedFactsContact | null
}

export interface ForecastFeedFacts {
  kind: 'forecast'
  forecast_id: number | null
  source_system: string | null
  agency: string | null
  title: string | null
  description: string | null
  anticipated_award_date: string | null
  fiscal_year: number | null
  naics: string | null
  status: string | null
  office: FeedFactsAgency | null
  // Flat strings upstream (heterogeneous agency forecast systems).
  set_aside: string | null
  place_of_performance: string | null
  estimated_period: string | null
  contract_vehicle: string | null
  primary_contact: FeedFactsContact | null
}

export interface EntityFeedFacts {
  kind: 'entity'
  uei: string | null
  legal_name: string | null
  dba_name: string | null
  description: string | null
  capabilities: string | null
  cage_code: string | null
  entity_url: string | null
  naics: string | null
}

/** A single grounding citation (`basis[].citations[]` on web facts): the source
 * that grounds a field, with any supporting excerpts pulled from it. */
export interface WebFactsCitation {
  title: string | null
  url: string | null
  excerpts: string[]
}

/** One web-facts grounding entry (a `basis` element): the model's reasoning,
 * confidence category, and citations for a single output field. Confidence is a
 * category label (e.g. "high"), never a numeric score. */
export interface WebFactsGroundingEntry {
  field: string | null
  reasoning: string | null
  confidence: string | null
  citations: WebFactsCitation[]
}

export interface WebFeedFacts {
  kind: 'web' | 'website'
  title: string | null
  url: string | null
  summary: string | null
  grounding: WebFactsGroundingEntry[]
}

export interface UnknownFeedFacts {
  kind: 'unknown'
  raw: Record<string, unknown>
}

export type MonitorFeedFacts =
  | ContractFeedFacts
  | IdvFeedFacts
  | OtaFeedFacts
  | OtidvFeedFacts
  | OpportunityFeedFacts
  | GrantFeedFacts
  | ForecastFeedFacts
  | EntityFeedFacts
  | WebFeedFacts
  | UnknownFeedFacts

export interface MonitorFeedResult {
  facts: MonitorFeedFacts
  changes: MonitorFieldChange[]
}

export interface MonitorFeedEntryEvent {
  provider: MonitorProvider
  provider_event_type: string
  raw_snapshot?: Record<string, unknown> | null
  result: MonitorFeedResult
}

export interface MonitorFeedEntryDetailOut extends MonitorFeedEntryOut {
  event: MonitorFeedEntryEvent | null
}

// ─────────────────────────────────────────────────────────────────────────────
// Resource lineage — a federal record's append-only snapshot history for one
// feed entry. Each step is the typed facts at that point plus the per-field
// diff against the immediately-older snapshot. `steps` is newest-first and
// empty for non-federal entries (no lineage chain yet).
// ─────────────────────────────────────────────────────────────────────────────

export interface MonitorLineageStep {
  occurred_at: Date | string
  is_current: boolean
  facts: MonitorFeedFacts
  changes: MonitorFieldChange[]
}

export interface MonitorFeedEntryLineageOut {
  resource_kind: string | null
  provider: MonitorProvider | null
  steps: MonitorLineageStep[]
}

// ─────────────────────────────────────────────────────────────────────────────
// Target search
// ─────────────────────────────────────────────────────────────────────────────

/** Department an office/agency hit nests under (mirrors backend TargetParent). */
export interface MonitorTargetParent {
  /** Department code (string), e.g. "97" — round-trips as the agency filter. */
  id: string
  /** Department name, e.g. "Department of Defense". */
  label: string
}

interface MonitorTargetHitBase {
  id: string
  label: string
  description: string | null
}

/** Full upstream payload for a search hit; shape varies by entity_type. */
export type MonitorTargetResource = Record<string, unknown>

export type MonitorDepartmentSource =
  | {
      source_type: 'department_lookup'
      resource: MonitorTargetResource
    }
  | {
      source_type: 'embedded_parent'
      resource: MonitorTargetResource
    }

export interface MonitorVendorTargetHit extends MonitorTargetHitBase {
  kind: 'vendor'
  entity_type: 'vendor'
  meta: null
  parent: null
  vendor: MonitorTargetResource
}

export interface MonitorAgencyDepartmentTargetHit extends MonitorTargetHitBase {
  kind: 'agency'
  entity_type: 'department'
  meta: null
  parent: null
  source: MonitorDepartmentSource
}

export interface MonitorAgencyOfficeTargetHit extends MonitorTargetHitBase {
  kind: 'agency'
  entity_type: 'office'
  meta: null
  parent: MonitorTargetParent | null
  agency: MonitorTargetResource
}

interface MonitorResourceTargetHitBase extends MonitorTargetHitBase {
  parent: null
}

export interface MonitorOpportunityTargetHit extends MonitorResourceTargetHitBase {
  kind: 'opportunity'
  entity_type: 'opportunity'
  meta: string | null
  opportunity: MonitorTargetResource
}

export interface MonitorContractTargetHit extends MonitorResourceTargetHitBase {
  kind: 'contract'
  entity_type: 'contract'
  meta: string | null
  contract: MonitorTargetResource
}

export interface MonitorIdvTargetHit extends MonitorResourceTargetHitBase {
  kind: 'idv'
  entity_type: 'idv'
  meta: string | null
  idv: MonitorTargetResource
}

export interface MonitorOtaTargetHit extends MonitorResourceTargetHitBase {
  kind: 'ota'
  entity_type: 'ota'
  meta: string | null
  ota: MonitorTargetResource
}

export interface MonitorOtidvTargetHit extends MonitorResourceTargetHitBase {
  kind: 'otidv'
  entity_type: 'otidv'
  meta: string | null
  otidv: MonitorTargetResource
}

export interface MonitorGrantTargetHit extends MonitorResourceTargetHitBase {
  kind: 'grant'
  entity_type: 'grant'
  meta: null
  grant: MonitorTargetResource
}

export interface MonitorForecastTargetHit extends MonitorResourceTargetHitBase {
  kind: 'forecast'
  entity_type: 'forecast'
  meta: null
  forecast: MonitorTargetResource
}

export type MonitorTargetHit =
  | MonitorVendorTargetHit
  | MonitorAgencyDepartmentTargetHit
  | MonitorAgencyOfficeTargetHit
  | MonitorOpportunityTargetHit
  | MonitorContractTargetHit
  | MonitorIdvTargetHit
  | MonitorOtaTargetHit
  | MonitorOtidvTargetHit
  | MonitorGrantTargetHit
  | MonitorForecastTargetHit

export interface MonitorTargetSearchList {
  count: number
  data: MonitorTargetHit[]
}

// ─────────────────────────────────────────────────────────────────────────────
// View-model types (frontend-derived, not wire)
// ─────────────────────────────────────────────────────────────────────────────

export type MonitorChannel = 'slack' | 'teams' | 'email'

export interface MonitorChip {
  key: string
  // List of distinct values for this filter. Always at least one entry. Each
  // entry is the human-readable value as it should be shown in the UI; do not
  // re-split on commas (entry text may itself contain commas, e.g. company
  // names like "Palantir USG, Inc").
  values: string[]
}

export interface MonitorOwner {
  // Null for the system owner (when the monitor's creator user is missing).
  userId: UUID | null
  name: string
}

export interface MonitorDashboardItem {
  id: UUID
  name: string
  chips: MonitorChip[]
  channels: MonitorChannel[]
  owner: MonitorOwner
  status: MonitorStatus
  unreadCount: number
  /** Raw 30 UTC-day feed-entry counts, oldest first (all statuses). */
  dailyCounts: readonly number[]
  /** UTC ISO timestamp of the newest feed entry (any status); null if none. */
  latestEntryAt: string | null
  /** Recipe target — drives monitor-level icons, labels, and filters. */
  targetKind: MonitorRecipeTarget | null
}
